<template>
  <div class="pluck" :class="{ extended: error || diffMode }">
    <div class="pluck-wrap">
      <div class="pluck-title">
        <ui-icon-tooltip :iconSize="'outline-white'" arrowTop arrowLeft>
          <span class="tooltip-image"
                :class="$i18n.locale"></span>
          <span class="tooltip-input">1 , 3 , 2</span>
          <span class="tooltip-text"
                v-html="$t('journey.pluck.guide')"></span>
        </ui-icon-tooltip>
        <i class="icon logo--pluck"></i>
      </div>
      <div class="pluck-input">
        <ui-input-text
          :type="'text'"
          :id="'pluck'"
          :autocomplete="'off'"
          :modelValue="pluckLang"
          :modelError="error"
          :modelDescription="diffMode ? $t('journey.pluck.description') : ''"
          :placeholder="$t('journey.pluck.placeholder')"
          :disabled="disabled"
          @input="setPluckLang"
          @onFocus="onEdit(true)"
          @onBlur="onEdit(false, $event)"
        />
      </div>
      <div class="pluck-buttons">
        <button :disabled="!canPluck"
              @click="onPluck">
          <i class="icon icon--plucking"></i>
          Plucking
        </button>
        <span class="gap"></span>
        <button :disabled="!canShare || isDemo"
                @click="onShare">
          <i class="icon icon--share"></i>
          Share
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import UiInputText from '@/components/ui/UiInputText'
import UiIconTooltip from '@/components/ui/UiIconTooltip'

export default {
  name: 'Pluck',
  components: {
    UiInputText,
    UiIconTooltip
  },
  props: [ 'activated', 'pluckError', 'disabled', 'tracking' ],
  computed: {
    ...mapState([
      'journeyPluck',
      'isDemo'
    ]),

    canPluck () {
      return !!this.pluckSeq.length && this.diffMode
    },
    canShare () {
      return !!this.pluckSeq.length && !this.tracking
    },
    pluckSeq () {
      return this.pluckLang.replace(/[^，\d]+/g, '').split('，').map(el => parseInt(el)).filter(el => !!el)
    },
    pluckTreeId () {
      return '0-0|' + this.pluckSeq.map((el, idx) => {
        return `${idx + 1}-${el}`
      }).join('|')
    },
    lastActivatedNode () {
      return this.activated.reduce((acc, cur) => cur || acc, null)
    },
    computedDiffMode () {
      return !!this.pluckSeq.length &&
        (!this.lastActivatedNode || this.lastActivatedNode.id !== this.pluckTreeId)
    }
  },
  watch: {
    disabled (value) {
      if (value) {
        this.pluckLang = ''
      }
    },
    computedDiffMode (value) {
      this.diffMode = value
      // setTimeout(() => {
      //   this.diffMode = value
      // }, 300)
    },
    lastActivatedNode (node) {
      if (node) {
        this.onInput(this.treeId2PluckLang(node.id))
      }
    },
    pluckError: {
      handler (value) {
        if (value) {
          this.error = value
          if (value === 'pluck.changeFrom1') {
            this.pluckLang = this.lastPluckLang
          }
        }
      },
      immediate: true
    }
  },
  created () {
    if (this.journeyPluck) {
      this.pluckLang = this.treeId2PluckLang(this.journeyPluck.treeId)
    } else {
      this.onInput(this.treeId2PluckLang(this.lastActivatedNode.id))
    }
    this.diffMode = this.computedDiffMode
  },
  methods: {
    treeId2PluckLang (treeId) {
      if (treeId) {
        const children = treeId.split('|').map(el => el.split('-')[1])
        children.shift()
        return children.join('，')
      }
      return ''
    },
    setPluckLang (value, e) {
      this.pluckLang = value
      setTimeout(() => {
        this.onInput(value, e)
      }, 10)
    },
    onInput (value, e) {
      if (value) {
        this.error = ''
      }
      const key = e ? e.which : null
      let caret = e ? e.target.selectionStart : null
      if (key === 37 || key === 39) {
        caret--
      }
      if (![8, 16, 17, 18, 91].includes(key)) {
        const filter1 = value.replace(/\D+/g, '，').replace(/，+/g, '，')
        if (filter1.length) {
          const filter2 = filter1.split('，')
          const filter4 = []
          let filter3 = null
          for (let [idx, number] of filter2.entries()) {
            if (idx < 25) {
              number = parseInt(number)
              if (number) {
                if (number > 25) {
                  let numberString = number.toString()
                  while (numberString.length) {
                    let cut = 2
                    if (parseInt(numberString.substr(0, 2)) > 25) {
                      cut = 1
                    }
                    filter4.push(numberString.substr(0, cut))
                    numberString = numberString.substr(cut, numberString.length)
                  }
                } else {
                  filter3 = parseInt(number)
                  filter4.push(filter3)
                }
              } else if (this.editing && filter2.length <= idx + 1) {
                filter4.push('')
              }
            }
          }
          if (this.editing && filter4[filter4.length - 1] &&
            filter4.length < 25 && filter3 > 2) {
            filter4.push('')
          }
          if (filter4.filter(el => !!el).length >= 25) {
            this.error = 'pluck.maximum25'
          }
          this.pluckLang = filter4.join('，').trim()
          if (caret !== null) {
            setTimeout(() => {
              e.target.setSelectionRange(caret + 1, caret + 1)
            }, 0)
          }
        } else {
          this.pluckLang = ''
        }
      } else {
        this.pluckLang = value
      }
    },
    onEdit (value, inputValue) {
      if (value && this.firstTime) {
        document.getElementById('pluck').blur()
        this.$emit('alerting', value, {
          title: this.$t('journey.pluck.notSync.title'),
          description: this.$t('journey.pluck.notSync.description'),
          cancel: this.$t('common.confirm'),
          onCancel: () => {
            this.firstTime = false
            document.getElementById('pluck').focus()
          }
        })
      }

      if (!value) {
        this.setPluckLang(inputValue || this.pluckLang)
      }
      this.editing = value
    },
    onPluck () {
      this.lastPluckLang = this.pluckLang
      this.$emit('loading', this.pluckTreeId)
    },
    onShare () {
      this.lastPluckLang = this.pluckLang
      this.$emit('sharing', this.pluckTreeId)
    }
  },
  data () {
    return {
      pluckLang: '',
      lastPluckLang: '',
      diffMode: false,

      error: '',
      editing: '',
      firstTime: true
    }
  }
}
</script>

<style lang="scss" scoped>
.pluck {
  width: 973px;
  height: 52px;
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
  background-color: #fff;
  transition: height 0.2s cubic-bezier(.28,.12,.42,.94);
  position: relative;

  .icon--beta {
    position: absolute;
    top: -2px;
    left: 70px;
  }

  &.extended {
    height: 70px;
  }

  .pluck-title {
    display: inline-block;
    vertical-align: middle;
    width: 135px;

    .tooltip {
      vertical-align: middle;
      margin: 0 5px 0 15px;

      & /deep/ .bubble {
        padding: 20px !important;
        margin-left: -3px;

        .arrow.top.left {
          left: 20px;
        }

        .text {
          text-align: left;

          .tooltip-image {
            display: block;
            width: 200px;
            height: 132px;
            margin: 0 auto;
            background: url('../../assets/img/journey/asset--sequence-en.svg') no-repeat;

            &.ko {
              background-image: url('../../assets/img/journey/asset--sequence-ko.svg');
            }

            &.ja {
              background-image: url('../../assets/img/journey/asset--sequence-ja.svg');
            }
          }

          .tooltip-input {
            display: block;
            margin: 30px 0 11px;
            padding: 3px 8px;
            border-radius: 5px;
            border: solid 2px #999;
            font-size: 13px;
            color: #ed7c00;
          }

          .tooltip-text {
            display: block;
            font-size: 12px;
            line-height: 1.5;
            color: #d34700;
            padding: 1px 4px;
          }
        }
      }
    }

    .icon {
      vertical-align: middle;
      margin-top: -2px;
    }
  }

  .pluck-input {
    display: inline-block;
    vertical-align: middle;
    width: 660px;
    padding: 11px 0;

    .input--wrap {
      margin-top: 0 !important;

      & /deep/ {
        label {
          display: none;
        }

        input {
          height: 32px;
          line-height: 28px;
        }

        .validation-text {
          top: calc(100% + 5px);
        }
      }
    }
  }

  .pluck-buttons {
    display: inline-block;
    vertical-align: middle;
    margin-left: 10px;

    button {
      vertical-align: middle;
      color: #444;
      font-size: 13px;
      padding: 5px 6px 5px 5px;
      border-radius: 4px;
      transition: background-color 0.15s ease,
                  opacity 0.3s ease;

      .icon {
        vertical-align: middle;
        margin-right: 3px;
      }

      &:disabled {
        opacity: 0.3;
      }

      &:not(:disabled) {
        &:hover,
        &:active {
          background-color: rgba(0, 0, 0, 0.08);
        }
      }
    }

    .gap {
      display: inline-block;
      vertical-align: middle;
      width: 1px;
      height: 12px;
      margin: 10px 4px;
      background-color: #d1d1d1;
    }
  }
}
</style>
